/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createAxiosInstance } from '../utils/Axios';
import { toast } from 'react-toastify';
import { useAuth, hasModuleAccess, hasSectionAccess } from '../utils/hooks/useAuth';

export default function CrmTasks() {
  const { access, loading: authLoading } = useAuth();

  // Check module and section permissions
  const hasCrmAccess = access ? hasModuleAccess('iurm', access) : false;
  const canViewTasks = access ? hasSectionAccess('iurm', 'Tasks', 'view', access) : false;
  const canCreateTask = access ? hasSectionAccess('iurm', 'Tasks', 'create', access) : false;
  const canEditTask = access ? hasSectionAccess('iurm', 'Tasks', 'edit', access) : false;
  const canDeleteTask = access ? hasSectionAccess('iurm', 'Tasks', 'delete', access) : false;

  // Check other CRM section permissions for tabs
  const canViewOverview = access ? hasSectionAccess('iurm', 'Overview', 'view', access) : false;
  const canViewLeads = access ? hasSectionAccess('iurm', 'Leads', 'view', access) : false;
  const canViewContacts = access ? hasSectionAccess('iurm', 'Contacts', 'view', access) : false;
  const canViewAccounts = access ? hasSectionAccess('iurm', 'Accounts', 'view', access) : false;
  const canViewCampaigns = access ? hasSectionAccess('iurm', 'Campaigns', 'view', access) : false;
  const canViewIntegration = access ? hasSectionAccess('iurm', 'Integration', 'view', access) : false;

  const [viewMode, setViewMode] = useState('table'); // 'table', 'kanban', or 'calendar'
  const [selectedTask, setSelectedTask] = useState(null);
  
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [stats, setStats] = useState({
    totalTasks: 0,
    pending: 0,
    completed: 0,
    overdue: 0
  });

  const kanbanStages = ['Pending', 'In Progress', 'Completed'];

  // Modal States
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', assignee: '', priority: 'Medium', date: '', status: 'Pending', contact: '', account: '', notes: '' });
  const [isSavingTask, setIsSavingTask] = useState(false);

  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editTaskData, setEditTaskData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  useEffect(() => {
    console.log('CRM Tasks Permissions:', {
      hasCrmAccess,
      canViewTasks,
      canCreateTask,
      canEditTask,
      canDeleteTask
    });
  }, [hasCrmAccess, canViewTasks, canCreateTask, canEditTask, canDeleteTask]);

  useEffect(() => {
    const fetchTasks = async () => {
      setIsLoading(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        
        let queryParams = `?limit=100`;
        if (searchTerm) queryParams += `&search=${searchTerm}`;

        const { data } = await axiosInstance.get(`${apiUrl}/tasks${queryParams}`);
        if (data && data.success) {
          setTasks(data.tasks || []);
          if (data.stats) setStats(data.stats);
        }
      } catch (error) {
        console.error("Failed to fetch tasks", error);
        toast.error("Failed to load tasks");
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchTasks();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, refreshTrigger]);

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;

    // Check edit permission
    if (!canEditTask) {
      toast.error("You don't have permission to update task status");
      return;
    }

    // Optimistic Update
    const originalTasks = [...tasks];
    setTasks(prev => prev.map(t => 
      t._id === taskId ? { ...t, status: targetStatus } : t
    ));

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      await axiosInstance.put(`${apiUrl}/tasks/${taskId}`, { status: targetStatus });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Failed to update task status", error);
      toast.error("Failed to update task status");
      setTasks(originalTasks); // Revert on failure
    }
  };

  const markComplete = async (taskId) => {
    if (!taskId) return;
    
    // Check edit permission
    if (!canEditTask) {
      toast.error("You don't have permission to complete tasks");
      return;
    }

    // Optimistic Update
    const originalTasks = [...tasks];
    setTasks(prev => prev.map(t => 
      t._id === taskId ? { ...t, status: 'Completed' } : t
    ));
    if (selectedTask) {
      setSelectedTask(prev => ({ ...prev, status: 'Completed' }));
    }

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      await axiosInstance.put(`${apiUrl}/tasks/${taskId}`, { status: 'Completed' });
      toast.success("Task marked as completed!");
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Failed to complete task", error);
      toast.error("Failed to complete task");
      setTasks(originalTasks); // Revert on failure
      if (selectedTask) {
        setSelectedTask(originalTasks.find(t => t._id === taskId) || selectedTask);
      }
    }
  };

  const handleSaveTask = async () => {
    if (!newTask.name) {
      toast.error("Task Name is required");
      return;
    }
    setIsSavingTask(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.post(`${apiUrl}/tasks`, newTask);
      
      toast.success("Task created successfully!");
      setShowAddTaskModal(false);
      setNewTask({ name: '', assignee: '', priority: 'Medium', date: '', status: 'Pending', contact: '', account: '', notes: '' });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Save failed", error);
      toast.error(error?.response?.data?.message || "Failed to create task");
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!editTaskData.name) {
      toast.error("Task Name is required");
      return;
    }
    setIsSavingEdit(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.put(`${apiUrl}/tasks/${editTaskData._id}`, editTaskData);
      
      toast.success("Task updated successfully!");
      setShowEditTaskModal(false);
      if (selectedTask?._id === editTaskData._id) {
        setSelectedTask({ ...selectedTask, ...editTaskData });
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Update failed", error);
      toast.error(error?.response?.data?.message || "Failed to update task");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;
    setIsDeletingTask(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.delete(`${apiUrl}/tasks/${deleteTaskId}`);
      
      toast.success("Task deleted successfully!");
      setShowDeleteTaskModal(false);
      if (selectedTask?._id === deleteTaskId) {
        setSelectedTask(null);
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Delete failed", error);
      toast.error(error?.response?.data?.message || "Failed to delete task");
    } finally {
      setIsDeletingTask(false);
      setDeleteTaskId(null);
    }
  };

  // Group tasks by day of week for the Calendar view
  const calendarDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const groupedCalendarTasks = tasks.reduce((acc, task) => {
    if (!task.date) {
      if (!acc['No Date']) acc['No Date'] = [];
      acc['No Date'].push(task);
      return acc;
    }
    const day = new Date(task.date).toLocaleDateString('en-US', { weekday: 'long' });
    if (!acc[day]) acc[day] = [];
    acc[day].push(task);
    return acc;
  }, {});

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Loading permissions...
      </div>
    );
  }

  // Show access denied if no module access or no tasks view access
  if (!hasCrmAccess || !canViewTasks) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '8px' }}>
          You don't have permission to access the Tasks section.
        </p>
        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
          Please contact your administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <div className="crm-tasks-container" style={{ padding: '16px', backgroundColor: '#f8f9fa', minHeight: '100%', fontFamily: 'Inter, sans-serif', position: 'relative' }}>
      
      {/* Sub-Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {canViewOverview && (
            <Link to="/crm/overview" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                Overview
              </div>
            </Link>
          )}
          {canViewLeads && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Leads
              </div>
            </Link>
          )}
          {canViewContacts && (
            <Link to="/crm/contacts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                Contacts
              </div>
            </Link>
          )}
          {canViewAccounts && (
            <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>
                Accounts
              </div>
            </Link>
          )}
          <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
            <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
            Tasks
          </div>
          {canViewCampaigns && (
            <Link to="/crm/campaigns" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                Campaigns
              </div>
            </Link>
          )}
          {canViewIntegration && (
            <Link to="/crm/integration" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 16V7a2 2 0 0 0-2-2h-4"></path><path d="M4 16V7a2 2 0 0 1 2-2h4"></path><rect x="9" y="15" width="6" height="6" rx="1"></rect><line x1="12" y1="5" x2="12" y2="15"></line></svg>
                MCP Integration
              </div>
            </Link>
          )}
        </div>
      </div>

      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0', color: '#111827', letterSpacing: '-0.5px' }}>Tasks</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Export</button>
          {canCreateTask && (
            <button onClick={() => setShowAddTaskModal(true)} style={{ padding: '8px 20px', backgroundColor: '#5B21B6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>+ New Task</button>
          )}
        </div>
      </div>
      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading Tasks...</div>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Name</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Assignee</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Date</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length > 0 ? tasks.map((task, idx) => (
                <tr key={task._id || idx} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#111827', fontWeight: '500' }}>{task.name}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563' }}>{task.assignee || '-'}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563' }}>{task.date || '-'}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563' }}>
                    <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: task.status === 'Completed' ? '#DEF7EC' : '#F3F4F6', color: task.status === 'Completed' ? '#03543F' : '#374151' }}>{task.status || '-'}</span>
                  </td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563', textAlign: 'right' }}>
                    {canEditTask && (
                      <button style={{ border: 'none', background: 'none', color: '#6B7280', cursor: 'pointer', marginRight: '12px' }} onClick={() => { setEditTaskData(task); setShowEditTaskModal(true); }}>Edit</button>
                    )}
                    {canDeleteTask && (
                      <button style={{ border: 'none', background: 'none', color: '#EF4444', cursor: 'pointer' }} onClick={() => { setDeleteTaskId(task._id); setShowDeleteTaskModal(true); }}>Delete</button>
                    )}
                    {canEditTask && task.status !== 'Completed' && (
                      <button style={{ border: 'none', background: 'none', color: '#10B981', cursor: 'pointer' }} onClick={() => markComplete(task._id)}>Complete</button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>No tasks found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {showAddTaskModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add Task</h3>
            <input placeholder="Name" value={newTask.name} onChange={e => setNewTask({...newTask, name: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Assignee" value={newTask.assignee} onChange={e => setNewTask({...newTask, assignee: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Date" type="date" value={newTask.date} onChange={e => setNewTask({...newTask, date: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowAddTaskModal(false)} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveTask} disabled={isSavingTask} style={{ padding: '8px 16px', background: '#5B21B6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

</div>);
}